"use client";

// The minimal previous-conversations switcher: a header-area button +
// dropdown listing the caller's recent chats (title + relative date) with
// a "New chat" action, driven by the injected `history` source prop
// (absent → no switcher). Per-row management actions (inline rename,
// confirm-delete — plan 028) render only when the host passes the matching
// callback; absent, rows are read-only as before. Deliberately NOT the
// full sidebar — no workspace grouping, share, search, or dialogs; those
// stay in the private layer (hosts with dialog needs mount their own via
// the same callbacks).

import type { SdkChatSummary } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { format, isSameYear, isToday, isYesterday } from "date-fns";
import {
  CheckIcon,
  ChevronDownIcon,
  HistoryIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useState } from "react";

/**
 * The history seam (`MagnusChatProps.history`). Absent → no switcher
 * button. `onSelect` reopens a past chat (the host loads its messages and
 * remounts the block on the new id); `onNewChat` resets to a fresh one.
 * `onRename`/`onDelete` (plan 028) are independently optional: pass one
 * and only that row action shows — a deliberate host product choice, not
 * a half-affordance (the block never invents a fallback for the other).
 */
export interface ChatHistorySource {
  /** Recent chats, most-recent first. */
  items: SdkChatSummary[];
  loading?: boolean;
  /** Present → per-row delete action (inline confirm, no dialog). */
  onDelete?: (chatId: string) => void;
  onNewChat: () => void;
  /** Present → per-row inline rename action (inline title edit, no dialog). */
  onRename?: (chatId: string, title: string) => void;
  onSelect: (chatId: string) => void;
  /**
   * Refetch the list. The switcher calls this on open so a failed or
   * stale mount-time load self-heals the next time the user looks.
   */
  refresh?: () => void;
}

/** Short context-aware label: "3:35 PM" / "Yesterday" / "May 23" / "May 23, 2024". */
function formatRelativeDay(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  if (isToday(date)) {
    return format(date, "h:mm a");
  }
  if (isYesterday(date)) {
    return "Yesterday";
  }
  if (isSameYear(date, new Date())) {
    return format(date, "MMM d");
  }
  return format(date, "MMM d, yyyy");
}

const ROW_ACTION_CLASS =
  "rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-foreground";

/** Header-area switcher for previous conversations. */
export function ChatHistorySwitcher({
  history,
  activeChatId,
}: {
  history: ChatHistorySource;
  activeChatId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const hasRowActions = Boolean(history.onRename ?? history.onDelete);

  const selectChat = (chatId: string) => {
    history.onSelect(chatId);
    setOpen(false);
  };

  const commitRename = (chat: SdkChatSummary) => {
    const title = editTitle.trim();
    // An empty or unchanged title is a cancel, not a server round trip.
    if (title && title !== chat.title) {
      history.onRename?.(chat.id, title);
    }
    setEditingId(null);
  };

  return (
    <DropdownMenu
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          // Revalidate on every open: the mount-time load may have failed
          // or gone stale since.
          history.refresh?.();
        } else {
          setEditingId(null);
          setConfirmingId(null);
        }
      }}
      open={open}
    >
      <DropdownMenuTrigger
        render={
          <Button
            aria-label="Previous conversations"
            className="gap-1.5 pr-2 text-muted-foreground"
            size="sm"
            type="button"
            variant="ghost"
          />
        }
      >
        <HistoryIcon className="size-4" />
        <span className="text-sm">Chats</span>
        <ChevronDownIcon className="size-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuItem onClick={() => history.onNewChat()}>
          <PlusIcon className="size-4" />
          <span>New chat</span>
        </DropdownMenuItem>
        {history.items.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                Recent
              </DropdownMenuLabel>
              <div className="max-h-72 overflow-y-auto">
                {history.items.map((chat) => {
                  const isActive = chat.id === activeChatId;
                  if (editingId === chat.id) {
                    return (
                      <div
                        className="flex items-center gap-1 px-2 py-1.5"
                        key={chat.id}
                      >
                        <input
                          aria-label="Chat title"
                          autoFocus
                          className="min-w-0 flex-1 rounded-sm bg-transparent px-1 py-0.5 text-sm outline-none ring-1 ring-ring"
                          onChange={(event) =>
                            setEditTitle(event.currentTarget.value)
                          }
                          onFocus={(event) => event.currentTarget.select()}
                          onKeyDown={(event) => {
                            // Keep menu typeahead/close keys out of the edit.
                            event.stopPropagation();
                            if (event.key === "Enter") {
                              commitRename(chat);
                            }
                            if (event.key === "Escape") {
                              setEditingId(null);
                            }
                          }}
                          value={editTitle}
                        />
                        <button
                          aria-label="Confirm rename"
                          className={ROW_ACTION_CLASS}
                          onClick={() => commitRename(chat)}
                          type="button"
                        >
                          <CheckIcon className="size-4" />
                        </button>
                        <button
                          aria-label="Cancel rename"
                          className={ROW_ACTION_CLASS}
                          onClick={() => setEditingId(null)}
                          type="button"
                        >
                          <XIcon className="size-4" />
                        </button>
                      </div>
                    );
                  }
                  if (confirmingId === chat.id) {
                    return (
                      <div
                        className="flex items-center gap-1 px-2 py-1.5 text-sm"
                        key={chat.id}
                      >
                        <span className="min-w-0 flex-1 truncate text-destructive">
                          Delete this chat?
                        </span>
                        <button
                          aria-label="Confirm delete"
                          className={ROW_ACTION_CLASS}
                          onClick={() => {
                            history.onDelete?.(chat.id);
                            setConfirmingId(null);
                          }}
                          type="button"
                        >
                          <CheckIcon className="size-4" />
                        </button>
                        <button
                          aria-label="Cancel delete"
                          className={ROW_ACTION_CLASS}
                          onClick={() => setConfirmingId(null)}
                          type="button"
                        >
                          <XIcon className="size-4" />
                        </button>
                      </div>
                    );
                  }
                  return (
                    <DropdownMenuItem
                      closeOnClick={hasRowActions ? false : undefined}
                      key={chat.id}
                      onClick={() => selectChat(chat.id)}
                    >
                      <span className="truncate">
                        {chat.title || "New chat"}
                      </span>
                      <span className="ml-auto shrink-0 pl-2 text-muted-foreground text-xs">
                        {formatRelativeDay(chat.updatedAt)}
                      </span>
                      {isActive && <CheckIcon className="size-4 shrink-0" />}
                      {history.onRename && (
                        <button
                          aria-label="Rename chat"
                          className={ROW_ACTION_CLASS}
                          onClick={(event) => {
                            event.stopPropagation();
                            setConfirmingId(null);
                            setEditTitle(chat.title);
                            setEditingId(chat.id);
                          }}
                          type="button"
                        >
                          <PencilIcon className="size-3.5" />
                        </button>
                      )}
                      {history.onDelete && (
                        <button
                          aria-label="Delete chat"
                          className={ROW_ACTION_CLASS}
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingId(null);
                            setConfirmingId(chat.id);
                          }}
                          type="button"
                        >
                          <Trash2Icon className="size-3.5" />
                        </button>
                      )}
                    </DropdownMenuItem>
                  );
                })}
                {history.loading && (
                  <div className="px-2 py-1.5 text-muted-foreground text-xs">
                    Loading…
                  </div>
                )}
              </div>
            </DropdownMenuGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
